import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { 
  Button, 
  Dialog, 
  DialogActions, 
  DialogContent, 
  DialogTitle, 
  IconButton,
  Tooltip,
  Typography,
  Box,
  Divider,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  InstallDesktop as InstallIcon,
  Info as InfoIcon,
  Smartphone as SmartphoneIcon,
  Android as AndroidIcon,
  Apple as AppleIcon,
  DesktopWindows as DesktopIcon,
  OpenInBrowser as BrowserIcon,
  Menu as MenuIcon,
  MoreVert as MoreVertIcon,
  AddToHomeScreen as AddToHomeScreenIcon
} from '@mui/icons-material';
import { usePWAInstall } from '../hooks/usePWAInstall';
import { checkPWASupport } from '../utils/pwaInstall';

/**
 * A button that allows users to install the PWA
 * Only shows when the app is not already installed and the browser supports PWA installation
 * 
 * @param {Object} props - Component props
 * @param {Object} props.deferredPrompt - The beforeinstallprompt event
 * @param {Function} props.onInstall - Callback when installation is successful
 * @param {string} [props.variant='contained'] - Button variant
 * @param {string} [props.color='primary'] - Button color
 * @param {string} [props.size='medium'] - Button size
 * @param {Object} [props.sx] - Additional styles
 */
const InstallButton = ({
  deferredPrompt,
  onInstall,
  variant = 'contained',
  color = 'primary',
  size = 'medium',
  sx = {},
  ...props
}) => {
  const { canInstall, isInstalled, isPWAInstallable } = usePWAInstall();
  const [debugInfo, setDebugInfo] = useState(null);
  const [showDebugInfo, setShowDebugInfo] = useState(false);
  const [showInstallInstructions, setShowInstallInstructions] = useState(false);

  // Log debug info on component mount
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      const info = checkPWASupport();
      setDebugInfo(info);
      console.log('PWA Debug Info:', info);
    }
  }, []);

  const handleInstallClick = async () => {
    if (canInstall && deferredPrompt) {
      try {
        // Show the install prompt
        deferredPrompt.prompt();
        
        // Wait for the user to respond to the prompt
        const { outcome } = await deferredPrompt.userChoice;
        
        // Log the user's choice
        console.log(`User ${outcome} the install prompt`);
        
        if (outcome === 'accepted') {
          console.log('User accepted the install prompt');
          if (onInstall) onInstall();
        } else {
          console.log('User dismissed the install prompt');
        }
      } catch (error) {
        console.error('Installation failed:', error);
        // Fallback for browsers that don't show the install prompt automatically
        setShowInstallInstructions(true);
      }
    } else if (onInstall) {
      onInstall();
    }
  };

  // Don't show the button if:
  // 1. The app is already installed
  // 2. The browser doesn't support PWA installation
  if (isInstalled || !isPWAInstallable) {
    return null;
  }

  // Get device type for better instructions
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  const isAndroid = /Android/i.test(navigator.userAgent);

  return (
    <>
      <Tooltip title="Install App" arrow>
        <Button
          variant={variant}
          color={color}
          size={size}
          onClick={handleInstallClick}
          startIcon={<InstallIcon />}
          sx={{ 
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: 1400,
            boxShadow: 3,
            ...sx 
          }}
          {...props}
        >
          Install App
        </Button>
      </Tooltip>

      {/* Debug Info Dialog */}
      {process.env.NODE_ENV === 'development' && debugInfo && (
        <>
          <Tooltip title="Show Debug Info" arrow>
            <IconButton
              onClick={() => setShowDebugInfo(true)}
              sx={{
                position: 'fixed',
                bottom: 24,
                right: 180,
                zIndex: 1400,
                bgcolor: 'background.paper',
                boxShadow: 3,
                '&:hover': {
                  bgcolor: 'action.hover',
                },
              }}
            >
              <InfoIcon />
            </IconButton>
          </Tooltip>
          
          <Dialog
            open={showDebugInfo}
            onClose={() => setShowDebugInfo(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>PWA Debug Information</DialogTitle>
            <DialogContent>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Installation Status
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                  <Chip
                    label={`Standalone: ${debugInfo.isStandalone ? 'Yes' : 'No'}`}
                    color={debugInfo.isStandalone ? 'success' : 'default'}
                    size="small"
                  />
                  <Chip
                    label={`Mobile: ${debugInfo.isMobile ? 'Yes' : 'No'}`}
                    color={debugInfo.isMobile ? 'info' : 'default'}
                    size="small"
                  />
                  <Chip
                    label={`HTTPS: ${debugInfo.isHTTPS ? 'Yes' : 'No'}`}
                    color={debugInfo.isHTTPS ? 'success' : 'error'}
                    size="small"
                  />
                </Box>
                <div>Display: {debugInfo.displayMode}</div>
              </Box>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
                Device Information
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    {isMobile ? <SmartphoneIcon /> : <DesktopIcon />}
                  </ListItemIcon>
                  <ListItemText 
                    primary={isMobile ? 'Mobile Device' : 'Desktop Device'} 
                    secondary={`${navigator.userAgent}`} 
                  />
                </ListItem>
                {isIOS && (
                  <ListItem>
                    <ListItemIcon>
                      <AppleIcon />
                    </ListItemIcon>
                    <ListItemText 
                      primary="iOS Device" 
                      secondary="For best experience, install this app to your home screen" 
                    />
                  </ListItem>
                )}
                {isAndroid && (
                  <ListItem>
                    <ListItemIcon>
                      <AndroidIcon />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Android Device" 
                      secondary="Install this app for a better experience" 
                    />
                  </ListItem>
                )}
              </List>
              <Divider sx={{ my: 2 }} />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2, fontStyle: 'italic' }}>
                Debug information is only shown in development mode.
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setShowDebugInfo(false)}>Close</Button>
            </DialogActions>
          </Dialog>
        </>
      )}

      {/* Installation Instructions Dialog */}
      <Dialog
        open={showInstallInstructions}
        onClose={() => setShowInstallInstructions(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Installation Instructions</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body1" gutterBottom>
              To install this app, follow these steps:
            </Typography>
            <List>
              {renderInstallationInstructions(isIOS, isAndroid)}
            </List>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Need help? Check your browser's documentation for "Add to Home Screen" instructions.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowInstallInstructions(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

// Render installation instructions based on device type
const renderInstallationInstructions = (isIOS, isAndroid) => {
  if (isIOS) {
    return (
      <>
        <ListItem>
          <ListItemIcon><MenuIcon /></ListItemIcon>
          <ListItemText 
            primary="1. Tap the Share button"
            secondary="It's the box with an arrow pointing up"
          />
        </ListItem>
        <ListItem>
          <ListItemIcon><AddToHomeScreenIcon /></ListItemIcon>
          <ListItemText 
            primary="2. Select 'Add to Home Screen'"
            secondary="Scroll down if needed to find this option"
          />
        </ListItem>
        <ListItem>
          <ListItemIcon><SmartphoneIcon /></ListItemIcon>
          <ListItemText 
            primary="3. Launch from Home Screen"
            secondary="Enjoy the app like a native experience"
          />
        </ListItem>
      </>
    );
  }

  if (isAndroid) {
    return (
      <>
        <ListItem>
          <ListItemIcon><MoreVertIcon /></ListItemIcon>
          <ListItemText 
            primary="1. Tap the menu button (⋮)"
            secondary="Located in the top-right corner of your browser"
          />
        </ListItem>
        <ListItem>
          <ListItemIcon><AddToHomeScreenIcon /></ListItemIcon>
          <ListItemText 
            primary="2. Select 'Add to Home screen'"
            secondary="Confirm the installation when prompted"
          />
        </ListItem>
      </>
    );
  }

  return (
    <>
      <ListItem>
        <ListItemIcon><BrowserIcon /></ListItemIcon>
        <ListItemText 
          primary="1. Look for the install button in your browser"
          secondary="It may appear in the address bar or browser menu"
        />
      </ListItem>
      <ListItem>
        <ListItemIcon><DesktopIcon /></ListItemIcon>
        <ListItemText 
          primary="2. Follow the on-screen instructions"
          secondary="The exact steps depend on your browser"
        />
      </ListItem>
    </>
  );
};

InstallButton.propTypes = {
  deferredPrompt: PropTypes.object,
  onInstall: PropTypes.func,
  variant: PropTypes.string,
  color: PropTypes.string,
  size: PropTypes.string,
  sx: PropTypes.object
};

export default InstallButton;
